import { getApiBaseUrl } from '../config';
import { LS_ORG_ID, LS_PRODUCT_ID, LS_SUPPORT_MODE, LS_SUPPORT_ROLE } from '../config/storageKeys';
import { supabase } from 'lib/supabase';

const GET_SESSION_TIMEOUT_MS = 10000;

/**
 * Fetch from the backend API. Path should start with /api/...
 * Automatically attaches JWT, organization ID, and product ID headers.
 * getSession() is raced with a timeout so a stuck Supabase client cannot block fetch() forever.
 */
export async function apiFetch(path: string, options?: RequestInit): Promise<Response> {
  const base = getApiBaseUrl();
  const url = base ? `${base}${path}` : path;
  const method = options?.method || 'GET';

  const headers = new Headers(options?.headers);

  let accessToken: string | undefined;
  try {
    const result = await Promise.race([
      supabase.auth.getSession(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('getSession_timeout')), GET_SESSION_TIMEOUT_MS)
      ),
    ]);
    accessToken = result.data.session?.access_token;
    if (!accessToken && method !== 'GET' && method !== 'HEAD') {
      console.warn('[apiFetch]', method, path, 'no session token — save may fail with 401');
    }
  } catch (e) {
    console.warn('[apiFetch]', method, path, 'getSession failed or timed out; request may 401', e);
  }

  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  // Caller may set X-Organization-Id (e.g. from React context); else use localStorage cache
  const orgId = headers.get('X-Organization-Id') || localStorage.getItem(LS_ORG_ID);
  if (orgId) {
    headers.set('X-Organization-Id', orgId);
  } else if (method !== 'GET' && method !== 'HEAD') {
    console.warn('[apiFetch]', method, path, 'missing X-Organization-Id — org API routes may fail');
  }

  const productId = headers.get('X-Product-Id') || localStorage.getItem(LS_PRODUCT_ID);
  if (productId) {
    headers.set('X-Product-Id', productId);
  }

  // Support impersonation: inject role override header when active
  if (localStorage.getItem(LS_SUPPORT_MODE) === 'true') {
    const supportRole = localStorage.getItem(LS_SUPPORT_ROLE);
    if (supportRole) {
      headers.set('X-Support-Effective-Role', supportRole);
    }
  }

  if (!headers.has('Content-Type') && options?.body && typeof options.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }

  if (method !== 'GET' && method !== 'HEAD') {
    // eslint-disable-next-line no-console
    console.log('[apiFetch] →', method, url);
  }
  return fetch(url, { ...options, headers });
}
